import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import { ArrowRepeat, Check2, Envelope, InfoCircle } from 'react-bootstrap-icons'
import axiosService from '../../../../../../utils/AxiosService'
import { toastSuccess, toastError, toastWarning } from '../../../../../FormComponents/Toasts'
import SendToClientModal from './SendToClientModal'
import DraftAssetPreview from '../../../../../Shared/DraftAssetPreview'
import PmChecklistReview from './PmChecklistReview'
import { uploadCopy } from '../../../../../../utils/actionDisplay'
import { useReadOnly } from '../../../../../../contexts/ReadOnlyContext'

export default observer(function SubmissionReview({ action }) {
  const { id, action_id } = useParams()
  const readOnly = useReadOnly()
  const { action_draft: actionDraft } = action
  const version = actionDraft.latestVersion
  // The client has already been sent this version - "Mark for Client" no
  // longer applies, and releasing feedback here is specifically "PM manages
  // client feedback" (tasklist #9), not the plain first-round revision path.
  const isClientReview = actionDraft.draft_status === 'client_review'
  // Already internally approved but not yet sent to a client - the only
  // valid next step from here (per the backend transition table) is Mark
  // for Client. Request Revision/Approve Internally don't accept this as a
  // starting state, so they're hidden rather than shown-and-guaranteed-to-fail.
  const isInternallyApproved = actionDraft.draft_status === 'internally_approved'
  const [feedback, setFeedback] = useState('')
  const [busy, setBusy] = useState(false)
  const [showSendToClientModal, setShowSendToClientModal] = useState(false)

  const base = `/api/project_manager/campaigns/${id}/actions/${action_id}/draft_versions/${version.id}`

  const isPmManaged = version.submitted_via === 'pm_on_behalf'

  const requestRevision = () => {
    if (!feedback.trim()) return toastError('Add feedback before requesting a revision')

    setBusy(true)
    axiosService.post(`${base}/request_revision`, { feedback })
      .then(({ influencer_notified }) => {
        actionDraft.draft_status = 'revision_requested'
        const label = isClientReview ? 'Feedback released to influencer' : 'Revision requested'
        influencer_notified || isPmManaged ? toastSuccess(label) : toastWarning(`${label} - this influencer has no email on file, so they were not notified automatically`)
      })
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setBusy(false))
  }

  const approveInternally = () => {
    setBusy(true)
    axiosService.post(`${base}/approve_internally`)
      .then(({ influencer_notified }) => {
        actionDraft.draft_status = 'internally_approved'
        influencer_notified || isPmManaged ? toastSuccess('Approved internally') : toastWarning('Approved internally - this influencer has no email on file, so they were not notified automatically')
      })
      .catch(({ data: { error } }) => toastError(error))
      .finally(() => setBusy(false))
  }

  const handleSentToClient = ({ client_recipient_emails, sent_to_client_at }) => {
    version.client_recipient_emails = client_recipient_emails
    version.sent_to_client_at = sent_to_client_at
    actionDraft.draft_status = 'client_review'
    setShowSendToClientModal(false)
    toastSuccess(`Sent to ${client_recipient_emails.join(', ')}`)
  }

  if (!version) return null

  // Sequential step numbers, matching the numbered sections in the Figma
  // reference - computed from whichever sections are actually visible for
  // this Action/state so a hidden one never leaves a gap in the sequence.
  const showUpload = actionDraft.fieldEnabled('upload') && version.draft_assets.length > 0
  const showCaption = actionDraft.fieldEnabled('caption') && version.caption
  const showLinksNotes = actionDraft.fieldEnabled('links_notes') && version.notes
  const showChecklist = actionDraft.fieldEnabled('checklist')
  const showClientFeedback = isClientReview && version.client_feedback.length > 0
  const showFeedbackInput = !readOnly && !isInternallyApproved
  const numberedSections = [
    ['upload', showUpload], ['caption', showCaption], ['links_notes', showLinksNotes],
    ['checklist', showChecklist], ['client_feedback', showClientFeedback], ['feedback', showFeedbackInput],
  ].filter(([, visible]) => visible).map(([key]) => key)
  const stepNumber = key => numberedSections.indexOf(key) + 1
  const groupUploadWithText = showUpload && (showCaption || showLinksNotes)
  const uploadInfo = uploadCopy(action.action_type)

  return (
    <div className="flex flex-col items-center">
      {(showUpload || showCaption || showLinksNotes) && (
        <div className={`border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode ${groupUploadWithText ? 'grid grid-cols-2 gap-6' : 'flex flex-col gap-4'}`}>
          {showUpload && (
            <div>
              <p className="font-bold mb-2 dark:text-white">
                {stepNumber('upload')}. {uploadInfo.label} {uploadInfo.formatNote && <span className="font-normal text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{uploadInfo.formatNote}</span>}
              </p>
              <div className="flex flex-wrap gap-3 mb-3">
                {version.draft_assets.map(asset => (
                  <DraftAssetPreview key={asset.id} asset={asset} className="max-h-60 rounded-lg" showActions />
                ))}
              </div>
            </div>
          )}

          {(showCaption || showLinksNotes) && (
            <div className="flex flex-col gap-4">
              {showCaption && (
                <div>
                  <p className="font-bold mb-2 dark:text-white">{stepNumber('caption')}. Add Caption</p>
                  <p className="dark:text-white whitespace-pre-line">{version.caption}</p>
                </div>
              )}

              {showLinksNotes && (
                <div>
                  <p className="font-bold mb-2 dark:text-white">{stepNumber('links_notes')}. Links / Notes</p>
                  <p className="dark:text-white whitespace-pre-line">{version.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showChecklist && <PmChecklistReview actionDraft={actionDraft} version={version} heading={`${stepNumber('checklist')}. Checklist`} />}

      {showClientFeedback && (
        <div className="border-1 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 w-[650px] mb-4">
          <p className="font-bold mb-2 text-amber-800 dark:text-amber-300">{stepNumber('client_feedback')}. Feedback from Client</p>
          <p className="flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400 mb-2">
            <InfoCircle className="shrink-0 mt-0.5" /> For your reference only - the influencer never sees this directly. Write your own feedback below and release it when ready.
          </p>
          {version.client_feedback.map(f => (
            <p key={f.id} className="text-gray-800 dark:text-white whitespace-pre-line mb-1 last:mb-0">{f.body}</p>
          ))}
        </div>
      )}

      {showFeedbackInput && (
        <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode">
          <p className="font-bold mb-2 dark:text-white">{stepNumber('feedback')}. {isClientReview ? 'Feedback to influencer' : 'Feedback on submission'}</p>
          <textarea
            rows={3}
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="Write your feedback or change request here"
            className="w-full p-2 border-1 border-rb-lite-grey rounded-lg focus:ring-transparent focus:border-rb-green dark:bg-rb-dark-mode dark:text-white dark:border-rb-dark-mode-lighter"
          />
        </div>
      )}

      {isClientReview && version.client_recipient_emails.length > 0 && (
        <div className="w-[650px] mb-4 flex items-start gap-2 rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter bg-white dark:bg-rb-dark-mode p-3 text-sm">
          <Envelope className="shrink-0 mt-0.5 text-rb-green" />
          <p className="dark:text-white">
            Sent to client{version.sent_to_client_at && <span className="text-rb-mat-grey dark:text-rb-dark-mode-text-grey"> on {new Date(version.sent_to_client_at).toLocaleString()}</span>}:{' '}
            <span className="font-semibold break-words">{version.client_recipient_emails.join(', ')}</span>
          </p>
        </div>
      )}

      <div className="w-[650px] mb-8 flex justify-between gap-3">
        {showFeedbackInput && (
          <button disabled={busy} onClick={requestRevision} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg disabled:opacity-50">
            <ArrowRepeat /> {isClientReview ? 'Release to Influencer' : 'Request Revision'}
          </button>
        )}
        {showFeedbackInput && (
          <button disabled={busy} onClick={approveInternally} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white disabled:opacity-50">
            <Check2 /> Approve Internally
          </button>
        )}
        {!readOnly && isInternallyApproved && (
          <button disabled={busy} onClick={() => setShowSendToClientModal(true)} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-rb-green rounded-lg disabled:opacity-50">
            <Envelope /> Send to Client
          </button>
        )}
      </div>

      <SendToClientModal
        isOpen={showSendToClientModal}
        onClose={() => setShowSendToClientModal(false)}
        baseUrl={base}
        onSent={handleSentToClient}
      />
    </div>
  )
})
